/**
 * Catalog Page - Products Management
 */

import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { PermissionGate } from "@/components/PermissionGate";
import { ProductsTable } from "@/components/products/ProductsTable";
import { ProductFormDialog } from "@/components/products/ProductFormDialog";
import { ProductDeleteDialog } from "@/components/products/ProductDeleteDialog";
import { ProductSuccessDialog } from "@/components/products/ProductSuccessDialog";
import { ProductViewDialog } from "@/components/products/ProductViewDialog";
import { Plus, Search, X } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { productsAdapter, categoriesAdapter } from "@/data";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ProductFormData } from "@/schemas/productSchema";
import { toast } from "@/hooks/use-toast";
import { v4 as uuid } from "uuid";

import { Product } from "@/types/product";

type SortField = "nome" | "categoria";
type SortDirection = "asc" | "desc" | null;

export default function Cadastro() {
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [categories, setCategories] = useState<Array<{ id_categoria: string; nome: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [lastCreatedProduct, setLastCreatedProduct] = useState<{ name: string } | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Load products and categories
  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const response = await productsAdapter.list({ limit: 100 });
      setProducts(response.items);
    } catch (error) {
      toast({
        title: "Erro ao carregar produtos",
        description: "Não foi possível carregar a lista de produtos.",
        variant: "destructive",
      });
      console.error("Error loading products:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesAdapter.list({ limit: 100 });
      setCategories(
        response.items
          .filter((c) => c.ativa)
          .map((c) => ({
            id_categoria: c.id_categoria,
            nome: c.nome,
          })),
      );
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort function
  const sortItems = (items: Product[]) => {
    if (!sortField || !sortDirection) return items;

    return [...items].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case "nome":
          aValue = a.nome;
          bValue = b.nome;
          break;
        case "categoria":
          aValue = a.categoria?.nome || "";
          bValue = b.categoria?.nome || "";
          break;
        default:
          return 0;
      }

      if (!aValue && aValue !== 0) return 1;
      if (!bValue && bValue !== 0) return -1;

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Filter products by search and category
  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.nome.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === "all" ? true : p.id_categoria === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  // Apply sorting to filtered products
  const sortedProducts = sortItems(filteredProducts);

  // Open create dialog
  const handleOpenCreate = () => {
    setFormMode("create");
    setSelectedProduct(null);
    setIsFormOpen(true);
  };

  // Open edit dialog
  const handleEdit = (product: Product) => {
    setFormMode("edit");
    setSelectedProduct(product);
    setIsFormOpen(true);
  };

  // Open delete dialog
  const handleDelete = (product: Product) => {
    setSelectedProduct(product);
    setIsDeleteOpen(true);
  };

  // Open view dialog
  const handleView = (product: Product) => {
    setSelectedProduct(product);
    setIsViewOpen(true);
  };

  // Submit form (create or edit)
  const handleFormSubmit = async (data: ProductFormData) => {
    setIsSubmitting(true);
    try {
      if (formMode === "create") {
        await productsAdapter.create(data, { idempotencyKey: uuid() });

        // Mostrar modal de sucesso
        setLastCreatedProduct({ name: data.name });
        setIsFormOpen(false);
        setIsSuccessOpen(true);

        await loadProducts();
      } else if (selectedProduct) {
        await productsAdapter.update(selectedProduct.id_produto, data);
        toast({
          title: "Produto atualizado",
          description: "As alterações foram salvas com sucesso.",
        });

        setIsFormOpen(false);
        await loadProducts();
      }
    } catch (error: any) {
      console.error("❌ [Cadastro] Erro ao salvar produto:", {
        error,
        mode: formMode,
        data,
        message: error?.message,
        code: error?.code,
        stack: error?.stack,
      });
      
      const errorMessage = error?.message || "Não foi possível salvar o produto.";

      toast({
        title: "Erro ao salvar produto",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!selectedProduct) return;

    try {
      setIsDeleting(true);
      console.log("🗑️ Deletando tipo produto:", selectedProduct);
      await productsAdapter.delete(selectedProduct.id_produto);
      toast({
        title: "✅ Produto excluído",
        description: `${selectedProduct.nome} foi removido do catálogo.`,
      });
      await loadProducts();
      setIsDeleteOpen(false);
    } catch (error: any) {
      console.error("❌ Erro ao deletar produto:", error);
      
      let errorMessage = "Não foi possível excluir o produto.";
      
      if (error?.message?.includes("vendas associadas")) {
        errorMessage = error.message;
      } else if (error?.message?.includes("policy") || error?.code === "42501") {
        errorMessage = "Você não tem permissão para excluir produtos.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao excluir produto",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Cadastro Tipos de Materiais</h1>
            <p className="text-muted-foreground">Gerencie seus produtos e variações</p>
          </div>
          <PermissionGate resource="products" action="create">
            <Button onClick={handleOpenCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Produto
            </Button>
          </PermissionGate>
        </div>

        {/* Filters Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search by Name/SKU */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Category Filter */}
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id_categoria} value={cat.id_categoria}>
                      {cat.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Active Filters Summary */}
            {(categoryFilter !== "all" || search) && (
              <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground flex-wrap">
                <span>Filtros ativos:</span>

                {categoryFilter !== "all" && (
                  <Badge variant="secondary" className="gap-2 pl-3 pr-1.5 py-1.5">
                    <span className="text-sm">{categories.find((c) => c.id_categoria === categoryFilter)?.nome}</span>
                    <button
                      onClick={() => setCategoryFilter("all")}
                      className="ml-1 hover:bg-muted/80 rounded-full p-1
                                 min-w-[28px] min-h-[28px] flex items-center justify-center
                                 transition-colors hover:text-destructive touch-manipulation"
                      aria-label="Remover filtro de categoria"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </Badge>
                )}

                {search && (
                  <Badge variant="secondary" className="gap-2 pl-3 pr-1.5 py-1.5">
                    <span className="text-sm">Busca: "{search}"</span>
                    <button
                      onClick={() => setSearch("")}
                      className="ml-1 hover:bg-muted/80 rounded-full p-1
                                 min-w-[28px] min-h-[28px] flex items-center justify-center
                                 transition-colors hover:text-destructive touch-manipulation"
                      aria-label="Limpar busca"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </Badge>
                )}

                <button
                  onClick={() => {
                    setCategoryFilter("all");
                    setSearch("");
                  }}
                  className="text-primary hover:underline ml-auto"
                >
                  Limpar todos
                </button>
              </div>
            )}

            {/* Results Counter */}
            <div className="text-sm text-muted-foreground mt-4">
              Exibindo <span className="font-medium">{filteredProducts.length}</span> de{" "}
              <span className="font-medium">{products.length}</span> produtos
            </div>
          </CardContent>
        </Card>

        {/* Products Table */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Carregando produtos..." />
          </div>
        ) : (
          <ProductsTable
            products={sortedProducts}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleView}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        )}
      </div>

      {/* Form Dialog */}
      <ProductFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        mode={formMode}
        product={selectedProduct}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Delete Dialog */}
      <ProductDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        product={selectedProduct}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      {/* View Dialog */}
      <ProductViewDialog
        open={isViewOpen}
        onOpenChange={setIsViewOpen}
        product={selectedProduct}
        onEdit={() => {
          setIsViewOpen(false);
          if (selectedProduct) handleEdit(selectedProduct);
        }}
      />

      {/* Success Dialog */}
      {lastCreatedProduct && (
        <ProductSuccessDialog
          open={isSuccessOpen}
          onOpenChange={setIsSuccessOpen}
          productName={lastCreatedProduct.name}
          onCreateNew={() => {
            setIsSuccessOpen(false);
            setFormMode("create");
            setSelectedProduct(null);
            setIsFormOpen(true);
          }}
          onBackToList={() => {
            setIsSuccessOpen(false);
            setLastCreatedProduct(null);
          }}
        />
      )}
    </Layout>
  );
}
